import { BookOpen } from 'lucide-react';

interface AdminGuideProps {
  currentUserRole: 'root' | 'admin';
}

export const AdminGuide = ({ currentUserRole: _currentUserRole }: AdminGuideProps) => {
  // Suppress unused variable warning - reserved for future role-based content
  void _currentUserRole;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
        <div className="flex items-center gap-3">
          <BookOpen size={28} className="text-blue-100" />
          <div>
            <h1 className="text-2xl font-bold">Admin Moderation Playbook</h1>
            <p className="text-blue-100 mt-1">
              Guidelines for reviewing, approving, and managing citizen-submitted reports
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 lg:p-8 prose prose-lg max-w-none">
        {/* Purpose Section */}
        <section className="mb-8">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Purpose</h2>
            <p className="text-gray-700">
              These playbooks guide administrators and moderators in reviewing, approving, rejecting, and managing citizen-submitted reports on VerifyPH.
            </p>
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-400 p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Core Principle</h3>
            <p className="text-gray-700 mb-2">
              VerifyPH documents citizen-submitted observations supported by evidence.
            </p>
            <p className="text-gray-700 font-semibold">
              Moderators do NOT judge legality, intent, or guilt.
            </p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Moderation focuses on:</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Safety</li>
              <li>Evidence presence</li>
              <li>Neutrality of language</li>
              <li>Platform integrity</li>
            </ul>
          </div>
        </section>

        {/* How to Use Section */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Use This Operationally</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Train new admins using Sections 2–4</li>
            <li>Use Section 7–8 during election periods</li>
            <li>Reference Section 9 during disputes</li>
            <li>Attach this doc to:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Legal responses</li>
                <li>Grant annexes</li>
                <li>Partner onboarding</li>
              </ul>
            </li>
          </ul>
        </section>

        {/* What This Gives You */}
        <section className="mb-8">
          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4">
            <h2 className="text-xl font-bold text-gray-900 mb-3">🔐 What This Gives You (Blunt Assessment)</h2>
            <p className="text-gray-700 mb-3">With these playbooks:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>A single rogue admin cannot sink the platform</li>
              <li>Moderation decisions are defensible and consistent</li>
              <li>You have written proof of neutrality</li>
              <li>You are insulated during elections</li>
            </ul>
          </div>
        </section>

        {/* Section 1: Moderator Roles */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Moderator Roles & Authority</h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Admin</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Review and moderate reports</li>
              <li>Approve, reject, or hide content</li>
              <li>Manage visibility</li>
              <li>Respond to disputes</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Root Admin</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>All Admin permissions</li>
              <li>Manage admin accounts</li>
              <li>Handle escalated legal or election-related cases</li>
              <li>Final authority on takedowns</li>
            </ul>
          </div>
        </section>

        {/* Section 2: Universal Moderation Checklist */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Universal Moderation Checklist (ALL REPORTS)</h2>
          <p className="text-gray-700 mb-4">Before approving any report, confirm:</p>
          
          <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 w-5 h-5 text-blue-600" disabled />
                <span className="text-gray-700">At least one piece of evidence (photo/video) is present</span>
              </li>
              <li className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 w-5 h-5 text-blue-600" disabled />
                <span className="text-gray-700">Evidence appears relevant to the report category</span>
              </li>
              <li className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 w-5 h-5 text-blue-600" disabled />
                <span className="text-gray-700">No explicit accusations (e.g., "corrupt", "illegal")</span>
              </li>
              <li className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 w-5 h-5 text-blue-600" disabled />
                <span className="text-gray-700">No hate speech, threats, or harassment</span>
              </li>
              <li className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 w-5 h-5 text-blue-600" disabled />
                <span className="text-gray-700">No personal data beyond what is visible in public space</span>
              </li>
              <li className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 w-5 h-5 text-blue-600" disabled />
                <span className="text-gray-700">Description is observational, not opinionated</span>
              </li>
            </ul>
            <p className="mt-4 text-gray-600 font-semibold">
              If ALL are satisfied → proceed to category-specific review.
            </p>
          </div>
        </section>

        {/* Section 3: Report Status Definitions */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Report Status Definitions</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Pending</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Newly submitted</li>
                <li>• Not visible to public</li>
              </ul>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Approved</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Meets evidence and neutrality requirements</li>
                <li>• Visible to public if marked public</li>
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Rejected</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Does not meet platform requirements</li>
                <li>• Stored for audit, not publicly visible</li>
              </ul>
            </div>

            <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Hidden (Admin-only)</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Previously approved but temporarily removed</li>
                <li>• Due to dispute, risk, or review</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 4: Category-Specific Playbooks */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Category-Specific Playbooks</h2>

          {/* 4.1 Anti-Epal Watch */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Anti-Epal Watch</h3>
            <p className="text-gray-700 mb-4 italic">
              <strong>Objective:</strong> Document observed promotional materials on government-funded projects.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">✅ Approve If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Evidence shows signage, billboards, tarpaulins, or markings</li>
                  <li>Material is visibly attached to or associated with public infrastructure</li>
                  <li>Description states only what is visible</li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">❌ Reject If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>No clear evidence of promotional material</li>
                  <li>Description includes accusations or intent</li>
                  <li>Image is unrelated or unverifiable</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded">
              <h4 className="font-semibold text-gray-900 mb-2">Required Public Disclaimer:</h4>
              <blockquote className="text-gray-700 italic border-l-4 border-yellow-500 pl-4">
                "This report documents an observed promotional material on a government-funded project. It does not determine legality or intent."
              </blockquote>
            </div>
          </div>

          {/* 4.2 Project Delay / Ghost Project */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 mb-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Project Delay / Ghost Project</h3>
            <p className="text-gray-700 mb-4 italic">
              <strong>Objective:</strong> Document observations suggesting lack of visible progress over time.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">✅ Approve If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Evidence shows location with limited or no visible activity</li>
                  <li>Timestamps or multiple reports provide context</li>
                  <li>Language remains descriptive</li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">❌ Reject If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Claims of fraud, theft, or corruption</li>
                  <li>Assertions about budgets or timelines without evidence</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
              <h4 className="font-semibold text-gray-900 mb-2">Moderator Note:</h4>
              <p className="text-gray-700">
                Never label a project as "delayed" or "abandoned". Use: <em>"Reports submitted after expected timeline"</em>.
              </p>
            </div>
          </div>

          {/* 4.3 Service Failure */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Service Failure</h3>
            <p className="text-gray-700 mb-4 italic">
              <strong>Objective:</strong> Document observed issues with public services.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">✅ Approve If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Evidence supports the observation (e.g., photos of broken facilities)</li>
                  <li>Report avoids blame or personal targeting</li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">❌ Reject If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Pure complaints without evidence</li>
                  <li>Abusive language toward individuals</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 4.4 Compliance / Transparency Gaps */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.4 Compliance / Transparency Gaps</h3>
            <p className="text-gray-700 mb-4 italic">
              <strong>Objective:</strong> Document missing signage or public information.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">✅ Approve If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Evidence shows absence of required public information</li>
                  <li>Description states what is missing, not why</li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">❌ Reject If:</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Assumptions about motives or violations</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Handling Comments */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Handling Comments</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">✅ Allowed:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Clarifying questions</li>
                <li>Additional observations</li>
                <li>Requests for context</li>
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">❌ Remove or Hide If:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Accusatory or defamatory language</li>
                <li>Political campaigning</li>
                <li>Harassment or threats</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded mt-4">
            <h3 className="font-semibold text-gray-900 mb-2">Comments should NEVER:</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Declare guilt</li>
              <li>Encourage harassment</li>
              <li>Call for punishment</li>
            </ul>
          </div>
        </section>

        {/* Section 6: Community Signals */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Community Signals (Votes & Flags)</h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Valid Votes</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Used as a visibility signal</li>
                <li>NOT a truth indicator</li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Abuse Flags</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Trigger review when threshold is reached</li>
                <li>Do NOT auto-hide content</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Moderator Action:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Review flagged reports promptly</li>
                <li>Add internal notes if no action is taken</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 7: Dispute & Takedown Requests */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Dispute & Takedown Requests</h2>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">When a Complaint Is Received:</h3>
            <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
              <li>Do NOT immediately remove content</li>
              <li>Review:
                <ul className="list-disc list-inside ml-6 mt-1">
                  <li>Evidence</li>
                  <li>Language</li>
                  <li>Moderation history</li>
                </ul>
              </li>
              <li>If risk exists:
                <ul className="list-disc list-inside ml-6 mt-1">
                  <li>Set report to Hidden</li>
                  <li>Escalate to Root Admin</li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 border-l-4 border-orange-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Grounds for Temporary Hiding:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Credible legal threat</li>
                <li>Election-period sensitivity</li>
                <li>Personal safety concerns</li>
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Grounds for Permanent Removal:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Proven false evidence</li>
                <li>Clear violation of platform rules</li>
                <li>Court or lawful order</li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-600 p-4 rounded mt-4">
            <p className="text-sm text-gray-700">
              <strong>Important:</strong> All actions must be logged in <code className="bg-gray-200 px-1 rounded">auditLogs</code>.
            </p>
          </div>
        </section>

        {/* Section 8: Election Period Protocol */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Election Period Protocol (High-Risk Mode)</h2>
          
          <div className="bg-red-50 border-l-4 border-red-600 p-5 rounded">
            <p className="text-gray-700 mb-4">During election periods:</p>
            
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Actions:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Increase scrutiny of language</li>
                <li>Prefer "Hide" over "Delete"</li>
                <li>Require Root Admin review for:
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>High-profile individuals</li>
                    <li>Media-linked reports</li>
                    <li>COMELEC-related complaints</li>
                  </ul>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-red-300 rounded p-4">
              <h3 className="font-semibold text-red-900 mb-2">Never:</h3>
              <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                <li>Mass-remove reports</li>
                <li>Alter data to favor any party</li>
                <li>Publish endorsements</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 9: Legal Safety Rules */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Legal Safety Rules (Non-Negotiable)</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Moderators must NEVER:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Declare something illegal</li>
                <li>Assign intent</li>
                <li>Suggest penalties</li>
                <li>Rank politicians or agencies</li>
              </ul>
            </div>

            <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Moderators SHOULD:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Stick to observable facts</li>
                <li>Reference evidence only</li>
                <li>Use platform disclaimers consistently</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 10: Audit & Accountability */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Audit & Accountability</h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <p className="text-gray-700 mb-4">Every moderation action must record:</p>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside mb-4">
              <li>Action taken</li>
              <li>Admin ID</li>
              <li>Timestamp</li>
              <li>Reason (short, factual)</li>
            </ul>

            <div className="bg-white border-l-4 border-blue-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Audit logs exist to:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Defend VerifyPH</li>
                <li>Protect moderators</li>
                <li>Ensure consistency</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 11: When in Doubt */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">11. When in Doubt</h2>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-5 rounded">
            <p className="text-lg font-semibold text-gray-900 mb-4">Default action:</p>
            <blockquote className="bg-white border-l-4 border-yellow-500 pl-4 py-2 mb-4 italic text-gray-700">
              <strong>Hide, escalate, document.</strong>
            </blockquote>

            <div className="bg-white border border-yellow-300 rounded p-4">
              <h3 className="font-semibold text-gray-900 mb-2">VerifyPH prioritizes:</h3>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Public safety</li>
                <li>Legal integrity</li>
                <li>Long-term trust</li>
              </ul>
              <p className="text-sm text-gray-700 mt-3 font-semibold">
                Speed is secondary to correctness.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
